import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Page, Header, Content } from '@backstage/core-components';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { ksdpComplianceMgmtEditPermission } from '@internal/ksdp-permissions-common';
import { COMPLIANCE_PAGE_STYLES } from '../styles';
import { BaselineRequirementsIcon, ItgcIcon, KoneCraIcon } from './icons';
import {
  Standard,
  StandardGroup,
  StandardRequirement,
  StandardsLibraryData,
} from '../types';

const EMPTY_DATA: StandardsLibraryData = {
  standards: [],
  appTypes: [],
  impactLevels: [],
  supportedStandards: {},
  baselineMatrix: {},
};

const ICONS: Record<string, ComponentType> = {
  'baseline-requirements': BaselineRequirementsIcon,
  itgc: ItgcIcon,
  'kone-cra': KoneCraIcon,
};

const GLYPHS: Record<string, string> = {
  'eu-nis2': '🇪🇺',
};

interface ApplicationRow {
  number?: string;
  ban?: string;
  name?: string;
  lifecycle?: string;
  security_impact?: number | null;
  business_unit?: string;
  department?: string;
}

interface Assignment {
  ban: string;
  applicationName?: string;
  lifecycle?: string;
}

/**
 * An application a Standard applies to without a stored assignment. Today
 * only Baseline Requirements has these: it applies through the
 * app-type/impact-level matrix rather than manual assignment.
 */
interface AutomaticAssignment {
  ban: string;
  applicationName?: string;
  lifecycle?: string;
  appType?: string;
  impactLevelId?: string;
}

interface AssignmentFilters {
  ban: string;
  name: string;
  lifecycle: string;
  securityImpact: string;
  businessUnit: string;
  department: string;
}

interface AssignmentFilterOptions {
  lifecycle: string[];
  securityImpact: Array<string | number>;
  businessUnit: string[];
  department: string[];
}

function formatUserRef(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return value.replace(/^user:default\//, '');
}

function sortItems<T extends { sortOrder: number; name: string }>(
  items: T[],
): T[] {
  return [...items].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function flattenGroups(
  groups: StandardGroup[],
  depth = 0,
): Array<{ group: StandardGroup; depth: number }> {
  return sortItems(groups).flatMap(group => [
    { group, depth },
    ...flattenGroups(group.groups ?? [], depth + 1),
  ]);
}

function countGroups(groups: StandardGroup[]): number {
  return groups.reduce(
    (count, group) => count + 1 + countGroups(group.groups ?? []),
    0,
  );
}

function CardIcon({ standard }: { standard: Standard }) {
  const Icon = ICONS[standard.slug];
  return Icon ? <Icon /> : <span>{GLYPHS[standard.slug] || '◈'}</span>;
}

export function CompliancePage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // UI-only gate: viewers can browse every Standard but see no create, edit,
  // transition, link or assignment controls. The backend router enforces
  // ksdp.compliance-mgmt.edit on every write route.
  const { allowed: canEdit } = usePermission({
    permission: ksdpComplianceMgmtEditPermission,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { standardId: routeStandardId } = useParams<{
    standardId?: string;
  }>();
  const standardId =
    routeStandardId ??
    location.pathname.split('/compliance-management/')[1]?.split('/')[0];
  const [data, setData] = useState<StandardsLibraryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showCreateStandard, setShowCreateStandard] = useState(false);
  const [showStandardEdit, setShowStandardEdit] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showRequirementForm, setShowRequirementForm] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingRequirementId, setEditingRequirementId] = useState<
    string | null
  >(null);
  const [showAssignments, setShowAssignments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkingRequirementId, setLinkingRequirementId] = useState<
    string | null
  >(null);
  const [linkTargetSearch, setLinkTargetSearch] = useState('');
  const [linkStandardFilter, setLinkStandardFilter] = useState('');
  const [linkGroupFilter, setLinkGroupFilter] = useState('');
  const [selectedLinkTarget, setSelectedLinkTarget] =
    useState<StandardRequirement | null>(null);
  const [useContentFrom, setUseContentFrom] = useState<string | null>(null);
  const [standardForm, setStandardForm] = useState({
    name: '',
    framework: '',
    version: '',
    owner: '',
    description: '',
  });
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    parentGroupId: '',
  });
  const [requirementForm, setRequirementForm] = useState({
    name: '',
    description: '',
    groupId: '',
  });
  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [automaticAssignments, setAutomaticAssignments] = useState<
    AutomaticAssignment[]
  >([]);
  const [selectedBans, setSelectedBans] = useState<Set<string>>(new Set());
  const [applicationSearch, setApplicationSearch] = useState('');
  const [applicationFilters, setApplicationFilters] =
    useState<AssignmentFilters>({
      ban: '',
      name: '',
      lifecycle: '',
      securityImpact: '',
      businessUnit: '',
      department: '',
    });
  const [applicationFilterOptions, setApplicationFilterOptions] =
    useState<AssignmentFilterOptions>({
      lifecycle: [],
      securityImpact: [],
      businessUnit: [],
      department: [],
    });
  const applicationFilterDebounceRef = useRef<number | null>(null);
  const assignmentRequestRef = useRef(0);
  const editFormRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (applicationFilterDebounceRef.current !== null) {
        window.clearTimeout(applicationFilterDebounceRef.current);
      }
      assignmentRequestRef.current += 1;
    };
  }, []);

  useEffect(() => {
    let frame: number | null = null;
    if (showGroupForm || showRequirementForm) {
      const form = editFormRef.current;
      if (form) {
        frame = window.requestAnimationFrame(() => {
          form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [
    showGroupForm,
    showRequirementForm,
    editingGroupId,
    editingRequirementId,
  ]);

  const DATA = data ?? EMPTY_DATA;
  const activeStandards = useMemo(
    () =>
      sortItems(
        DATA.standards.filter(standard => standard.status === 'Active'),
      ),
    [DATA.standards],
  );
  const currentStandard = DATA.standards.find(
    standard => standard.standardId === standardId,
  );
  const groupOptions = currentStandard
    ? flattenGroups(currentStandard.groups)
    : [];
  const groupOptionsForForm = groupOptions.filter(({ group }) => {
    if (group.status === 'Archived') return false;
    if (!editingGroupId || group.groupId === editingGroupId)
      return !editingGroupId;
    const groupsById = new Map(
      groupOptions.map(option => [option.group.groupId, option.group]),
    );
    const visited = new Set<string>();
    let parentId = group.parentGroupId;
    while (parentId && !visited.has(parentId)) {
      if (parentId === editingGroupId) return false;
      visited.add(parentId);
      parentId = groupsById.get(parentId)?.parentGroupId ?? null;
    }
    return true;
  });
  const selectableGroupOptions = groupOptions.filter(
    ({ group }) => group.status !== 'Archived',
  );

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('compliance-management');
      const response = await fetchApi.fetch(
        `${baseUrl}/standards-library?includeArchived=true`,
      );
      if (!response.ok)
        throw new Error(
          `Failed to load Standards Library (${response.status})`,
        );
      setData((await response.json()) as StandardsLibraryData);
      setError(null);
    } catch {
      setError('Could not load the Standards Library.');
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    void loadLibrary();
  }, [loadLibrary]);

  const navigateTo = (targetStandardId: string) => {
    assignmentRequestRef.current += 1;
    if (applicationFilterDebounceRef.current !== null) {
      window.clearTimeout(applicationFilterDebounceRef.current);
      applicationFilterDebounceRef.current = null;
    }
    navigate(
      targetStandardId === 'dashboard'
        ? '/compliance-management'
        : `/compliance-management/${targetStandardId}`,
    );
    setExpanded(new Set());
    setShowCreateStandard(false);
    setShowStandardEdit(false);
    setShowGroupForm(false);
    setShowRequirementForm(false);
    setEditingGroupId(null);
    setEditingRequirementId(null);
    setShowAssignments(false);
    setFormError(null);
  };

  const toggle = (id: string) => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (path: string, method: string, body?: unknown) => {
    const baseUrl = await discoveryApi.getBaseUrl('compliance-management');
    const response = await fetchApi.fetch(`${baseUrl}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const responseBody = await response.json().catch(() => ({}));
      throw new Error(
        responseBody.error || `Request failed (${response.status})`,
      );
    }
    return response.json();
  };

  const createStandard = async () => {
    if (!standardForm.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const created = (await submit('/standards', 'POST', {
        name: standardForm.name,
        framework: standardForm.framework,
        version: standardForm.version,
        owner: standardForm.owner,
        description: standardForm.description,
      })) as Standard;
      setStandardForm({
        name: '',
        framework: '',
        version: '',
        owner: '',
        description: '',
      });
      setShowCreateStandard(false);
      await loadLibrary();
      if (created.standardId) navigateTo(created.standardId);
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not create Standard.',
      );
    } finally {
      setSaving(false);
    }
  };

  const updateStandard = async () => {
    if (!currentStandard || !standardForm.name.trim()) {
      setFormError('Standard name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/standards/${currentStandard.standardId}`, 'PATCH', {
        name: standardForm.name,
        framework: standardForm.framework,
        version: standardForm.version,
        owner: standardForm.owner,
        description: standardForm.description,
      });
      setShowStandardEdit(false);
      await loadLibrary();
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not update Standard.',
      );
    } finally {
      setSaving(false);
    }
  };

  const transitionStandard = async (
    status: 'Draft' | 'Active' | 'Archived',
  ) => {
    if (!currentStandard) return;
    if (status !== 'Active') {
      const message =
        status === 'Archived'
          ? `Archive Standard "${currentStandard.name}"? Existing assignments and assessment data will be preserved.`
          : `Restore Standard "${currentStandard.name}" as ${status}?`;
      if (!window.confirm(message)) return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/standards/${currentStandard.standardId}/status`, 'PATCH', {
        status,
      });
      if (status === 'Archived') {
        navigateTo('dashboard');
      } else {
        await loadLibrary();
      }
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : `Could not set Standard status to ${status}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteDraftStandard = async () => {
    const standard = currentStandard;
    if (!standard || standard.status !== 'Draft') return;
    if (
      !window.confirm(
        `Delete Draft Standard "${standard.name}"? This permanently deletes the Standard, its Controls, Requirements, assignments, and status history.`,
      )
    )
      return;
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/standards/${standard.standardId}`, 'DELETE');
      await loadLibrary();
      navigateTo('dashboard');
    } catch (deleteError) {
      setFormError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Could not delete Draft Standard.',
      );
    } finally {
      setSaving(false);
    }
  };

  const startStandardEdit = (standard: Standard) => {
    setStandardForm({
      name: standard.name,
      framework: standard.framework,
      version: standard.version,
      owner: standard.owner,
      description: standard.description,
    });
    setShowStandardEdit(true);
    setShowGroupForm(false);
    setShowRequirementForm(false);
    setShowAssignments(false);
    setFormError(null);
  };

  const saveGroup = async () => {
    if (!currentStandard || !groupForm.name.trim()) {
      setFormError('Control name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editingGroupId) {
        await submit(`/groups/${editingGroupId}`, 'PATCH', {
          name: groupForm.name,
          description: groupForm.description,
          parentGroupId: groupForm.parentGroupId || null,
        });
      } else {
        await submit(
          `/standards/${currentStandard.standardId}/groups`,
          'POST',
          {
            name: groupForm.name,
            description: groupForm.description,
            parentGroupId: groupForm.parentGroupId || null,
          },
        );
      }
      setGroupForm({ name: '', description: '', parentGroupId: '' });
      setEditingGroupId(null);
      setShowGroupForm(false);
      await loadLibrary();
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not save Control.',
      );
    } finally {
      setSaving(false);
    }
  };

  const startGroupEdit = (group: StandardGroup) => {
    setGroupForm({
      name: group.name,
      description: group.description,
      parentGroupId: group.parentGroupId ?? '',
    });
    setEditingGroupId(group.groupId);
    setShowGroupForm(true);
    setShowRequirementForm(false);
    setShowStandardEdit(false);
    setShowAssignments(false);
    setFormError(null);
  };

  const startChildGroup = (parent: StandardGroup) => {
    setGroupForm({
      name: '',
      description: '',
      parentGroupId: parent.groupId,
    });
    setEditingGroupId(null);
    setShowGroupForm(true);
    setShowRequirementForm(false);
    setShowStandardEdit(false);
    setShowAssignments(false);
    setFormError(null);
  };

  const saveRequirement = async () => {
    if (
      !currentStandard ||
      !requirementForm.name.trim() ||
      !requirementForm.description.trim()
    ) {
      setFormError('Requirement name and description are required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        name: requirementForm.name,
        description: requirementForm.description,
        groupId: requirementForm.groupId || null,
      };
      if (editingRequirementId) {
        await submit(`/requirements/${editingRequirementId}`, 'PATCH', payload);
      } else {
        await submit(
          `/standards/${currentStandard.standardId}/requirements`,
          'POST',
          payload,
        );
      }
      setRequirementForm({
        name: '',
        description: '',
        groupId: '',
      });
      setEditingRequirementId(null);
      setShowRequirementForm(false);
      await loadLibrary();
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not save Requirement.',
      );
    } finally {
      setSaving(false);
    }
  };

  const startRequirementEdit = (requirement: StandardRequirement) => {
    setRequirementForm({
      name: requirement.name,
      description: requirement.description,
      groupId: requirement.groupId ?? '',
    });
    setEditingRequirementId(requirement.requirementId);
    setShowRequirementForm(true);
    setShowGroupForm(false);
    setShowStandardEdit(false);
    setShowAssignments(false);
    setFormError(null);
  };

  const loadAssignments = async (
    filters: AssignmentFilters = applicationFilters,
  ) => {
    const requestId = ++assignmentRequestRef.current;
    if (applicationFilterDebounceRef.current !== null) {
      window.clearTimeout(applicationFilterDebounceRef.current);
      applicationFilterDebounceRef.current = null;
    }
    if (!currentStandard) return;
    try {
      const businessApplicationsBaseUrl = await discoveryApi.getBaseUrl(
        'business-applications',
      );
      const complianceManagementBaseUrl = await discoveryApi.getBaseUrl(
        'compliance-management',
      );
      const query = new URLSearchParams({ limit: '100' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) query.set(key, value);
      });
      const [appsResponse, assignmentsResponse, filterOptionsResponse] =
        await Promise.all([
          fetchApi.fetch(
            `${businessApplicationsBaseUrl}/applications?${query.toString()}`,
          ),
          fetchApi.fetch(
            `${complianceManagementBaseUrl}/standards/${currentStandard.standardId}/assignments`,
          ),
          fetchApi.fetch(`${businessApplicationsBaseUrl}/applications/filters`),
        ]);
      const appsBody = appsResponse.ok
        ? await appsResponse.json()
        : { data: [] };
      const assignmentBody = assignmentsResponse.ok
        ? await assignmentsResponse.json()
        : { assignments: [], automaticAssignments: [] };
      const filterBody = filterOptionsResponse.ok
        ? await filterOptionsResponse.json()
        : null;
      if (requestId !== assignmentRequestRef.current) return;
      if (filterBody) {
        setApplicationFilterOptions({
          lifecycle: (filterBody.lifecycle ?? []) as string[],
          securityImpact: (filterBody.securityImpact ?? []) as Array<
            string | number
          >,
          businessUnit: (filterBody.businessUnit ?? []) as string[],
          department: (filterBody.department ?? []) as string[],
        });
      }
      setApplications((appsBody.data ?? []) as ApplicationRow[]);
      setAssignments((assignmentBody.assignments ?? []) as Assignment[]);
      setAutomaticAssignments(
        (assignmentBody.automaticAssignments ?? []) as AutomaticAssignment[],
      );
      setSelectedBans(
        new Set(
          (assignmentBody.assignments ?? []).map(
            (assignment: Assignment) => assignment.ban,
          ),
        ),
      );
    } catch (assignmentError) {
      if (requestId !== assignmentRequestRef.current) return;
      setFormError(
        assignmentError instanceof Error
          ? assignmentError.message
          : 'Could not load assignments.',
      );
    }
  };

  const updateApplicationFilter = (
    key: keyof AssignmentFilters,
    value: string,
  ) => {
    const nextFilters = { ...applicationFilters, [key]: value };
    setApplicationFilters(nextFilters);
    assignmentRequestRef.current += 1;

    if (key === 'ban' || key === 'name') {
      if (applicationFilterDebounceRef.current !== null) {
        window.clearTimeout(applicationFilterDebounceRef.current);
      }
      applicationFilterDebounceRef.current = window.setTimeout(() => {
        applicationFilterDebounceRef.current = null;
        void loadAssignments(nextFilters);
      }, 500);
      return;
    }

    if (applicationFilterDebounceRef.current !== null) {
      window.clearTimeout(applicationFilterDebounceRef.current);
      applicationFilterDebounceRef.current = null;
    }
    void loadAssignments(nextFilters);
  };

  const toggleAssignment = (ban: string) => {
    setSelectedBans(previous => {
      const next = new Set(previous);
      if (next.has(ban)) next.delete(ban);
      else next.add(ban);
      return next;
    });
  };

  const saveAssignments = async () => {
    if (!currentStandard) return;
    setSaving(true);
    setFormError(null);
    try {
      const assignmentPath = `/standards/${currentStandard.standardId}/assignments`;
      const selected = Array.from(selectedBans);
      const currentlyAssigned = new Set(
        assignments.map(assignment => assignment.ban),
      );
      const removed = Array.from(currentlyAssigned).filter(
        ban => !selectedBans.has(ban),
      );

      if (selected.length > 0) {
        await submit(assignmentPath, 'POST', { bans: selected });
      }
      await Promise.all(
        removed.map(ban =>
          submit(`${assignmentPath}/${encodeURIComponent(ban)}`, 'DELETE'),
        ),
      );
      await loadAssignments();
    } catch (saveError) {
      setFormError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not save assignments.',
      );
    } finally {
      setSaving(false);
    }
  };

  const transitionGroup = async (
    group: StandardGroup,
    status: 'Active' | 'Draft' | 'Archived',
  ) => {
    const message =
      status === 'Archived'
        ? `Archive control "${group.name}" and all nested child controls and requirements?`
        : `Restore control "${group.name}" and its archived contents as ${status}?`;
    if (!window.confirm(message)) return;
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/groups/${group.groupId}`, 'PATCH', { status });
      await loadLibrary();
    } catch (transitionError) {
      setFormError(
        transitionError instanceof Error
          ? transitionError.message
          : `Could not set Control status to ${status}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteDraftGroup = async (group: StandardGroup) => {
    if (
      !window.confirm(
        `Delete Draft Control "${group.name}"? Delete its child Controls and Requirements first.`,
      )
    )
      return;
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/groups/${group.groupId}`, 'DELETE');
      await loadLibrary();
    } catch (deleteError) {
      setFormError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Could not delete Draft Control.',
      );
    } finally {
      setSaving(false);
    }
  };

  const transitionRequirement = async (
    requirement: StandardRequirement,
    status: 'Active' | 'Draft' | 'Archived',
  ) => {
    const message =
      status === 'Archived'
        ? `Archive requirement "${requirement.name}"?`
        : `Restore requirement "${requirement.name}" as ${status}?`;
    if (!window.confirm(message)) return;
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/requirements/${requirement.requirementId}`, 'PATCH', {
        status,
      });
      await loadLibrary();
    } catch (transitionError) {
      setFormError(
        transitionError instanceof Error
          ? transitionError.message
          : `Could not set Requirement status to ${status}.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteDraftRequirement = async (requirement: StandardRequirement) => {
    if (
      !window.confirm(
        `Delete Draft Requirement "${requirement.name}" permanently?`,
      )
    )
      return;
    setSaving(true);
    setFormError(null);
    try {
      await submit(`/requirements/${requirement.requirementId}`, 'DELETE');
      await loadLibrary();
    } catch (deleteError) {
      setFormError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Could not delete Draft Requirement.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Admin-only action pending real permissions framework
  const startLinking = (requirement: StandardRequirement) => {
    setLinkingRequirementId(requirement.requirementId);
    setSelectedLinkTarget(null);
    setUseContentFrom(null);
    setLinkTargetSearch('');
    setLinkStandardFilter('');
    setLinkGroupFilter('');
    setShowLinkPicker(true);
  };

  const changeLinkStandardFilter = (value: string) => {
    setLinkStandardFilter(value);
    setLinkGroupFilter('');
    setSelectedLinkTarget(null);
    setUseContentFrom(null);
  };

  const changeLinkGroupFilter = (value: string) => {
    setLinkGroupFilter(value);
    setSelectedLinkTarget(null);
    setUseContentFrom(null);
  };

  // Admin-only action pending real permissions framework
  const confirmLink = async () => {
    if (!linkingRequirementId || !selectedLinkTarget || !useContentFrom) return;

    setSaving(true);
    setFormError(null);
    try {
      await submit(`/requirements/${linkingRequirementId}/links`, 'POST', {
        targetRequirementId: selectedLinkTarget.requirementId,
        useContentFrom,
      });
      await loadLibrary();
      setShowLinkPicker(false);
      setLinkingRequirementId(null);
      setSelectedLinkTarget(null);
      setUseContentFrom(null);
    } catch (linkError) {
      setFormError(
        linkError instanceof Error
          ? linkError.message
          : 'Could not link requirements.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Admin-only action pending real permissions framework
  const unlinkRequirement = async (requirement: StandardRequirement) => {
    if (
      !window.confirm(
        `Unlink "${requirement.name}" from its linked requirements? This will stop future content and status syncing.`,
      )
    )
      return;

    setSaving(true);
    setFormError(null);
    try {
      await submit(
        `/requirements/${requirement.requirementId}/links`,
        'DELETE',
      );
      await loadLibrary();
    } catch (unlinkError) {
      setFormError(
        unlinkError instanceof Error
          ? unlinkError.message
          : 'Could not unlink requirement.',
      );
    } finally {
      setSaving(false);
    }
  };

  // Get all requirements from other standards for link target selection
  const getOtherStandardRequirements = (): StandardRequirement[] => {
    const otherRequirements: StandardRequirement[] = [];
    DATA.standards.forEach(standard => {
      if (standard.standardId !== currentStandard?.standardId) {
        // Flatten requirements from groups and top-level
        const addRequirements = (reqs: StandardRequirement[]) => {
          reqs.forEach(req =>
            otherRequirements.push({
              ...req,
              standardName: standard.name,
            } as any),
          );
        };
        const addFromGroups = (groups: StandardGroup[]) => {
          groups.forEach(group => {
            addRequirements(group.requirements || []);
            addFromGroups(group.groups || []);
          });
        };
        addRequirements(standard.requirements || []);
        addFromGroups(standard.groups || []);
      }
    });
    return otherRequirements;
  };

  // Link picker dropdowns: active standards other than the current one, and
  // the non-archived controls of the chosen standard.
  const linkStandardOptions = activeStandards.filter(
    standard => standard.standardId !== currentStandard?.standardId,
  );
  const linkControlOptions = (() => {
    const chosen = linkStandardOptions.find(
      standard => standard.standardId === linkStandardFilter,
    );
    return chosen
      ? flattenGroups(chosen.groups).filter(
          ({ group }) => group.status !== 'Archived',
        )
      : [];
  })();

  // Requirements to show in the picker list, narrowed by the dropdowns and
  // then by the search text. A chosen control includes its nested controls.
  const getLinkTargetCandidates = (): StandardRequirement[] => {
    let candidates = getOtherStandardRequirements();
    if (linkStandardFilter) {
      candidates = candidates.filter(
        req => req.standardId === linkStandardFilter,
      );
    }
    if (linkGroupFilter) {
      const chosenGroup = linkControlOptions.find(
        ({ group }) => group.groupId === linkGroupFilter,
      )?.group;
      const allowedIds = new Set<string>();
      const collect = (group: StandardGroup) => {
        (group.requirements || []).forEach(req =>
          allowedIds.add(req.requirementId),
        );
        (group.groups || []).forEach(collect);
      };
      if (chosenGroup) collect(chosenGroup);
      candidates = candidates.filter(req => allowedIds.has(req.requirementId));
    }
    const query = linkTargetSearch.trim().toLowerCase();
    if (!query) return candidates;
    return candidates.filter(
      req =>
        req.name.toLowerCase().includes(query) ||
        req.ref.toLowerCase().includes(query),
    );
  };

  const renderRequirement = (requirement: StandardRequirement) => {
    const childActionsAllowed =
      canEdit && currentStandard?.status !== 'Archived';
    return (
      <div className="compliance-library-entry" key={requirement.requirementId}>
        <div className="compliance-item-title-row">
          <strong>{requirement.name}</strong>
          <span className="compliance-item-badge compliance-item-badge--required">
            {requirement.status}
          </span>
        </div>
        <p className="compliance-requirement-desc">{requirement.description}</p>
        {requirement.links?.length > 0 && (
          <ul className="compliance-item-links">
            {requirement.links.map(link => (
              <li key={`${requirement.requirementId}-${link.url}`}>
                <a href={link.url} target="_blank" rel="noreferrer">
                  {link.text}
                </a>
              </li>
            ))}
          </ul>
        )}
        {childActionsAllowed && (
          <div className="inline-actions">
            {requirement.status !== 'Archived' && (
              <button
                type="button"
                className="action-button secondary-button"
                onClick={() => startRequirementEdit(requirement)}
              >
                Edit
              </button>
            )}
            {requirement.status !== 'Archived' && (
              <>
                {/* Admin-only action pending real permissions framework */}
                {!requirement.linkedRequirements?.length ? (
                  <button
                    type="button"
                    className="action-button secondary-button"
                    onClick={() => startLinking(requirement)}
                    disabled={saving}
                  >
                    Link to another standard
                  </button>
                ) : (
                  <button
                    type="button"
                    className="action-button secondary-button"
                    onClick={() => unlinkRequirement(requirement)}
                    disabled={saving}
                  >
                    Linked ({requirement.linkedRequirements.length}) · Revert
                  </button>
                )}
              </>
            )}
            {requirement.status === 'Draft' && (
              <>
                <button
                  type="button"
                  className="action-button"
                  disabled={saving}
                  onClick={() =>
                    void transitionRequirement(requirement, 'Active')
                  }
                >
                  Activate
                </button>
                <button
                  type="button"
                  className="action-button danger-button"
                  disabled={saving}
                  onClick={() => void deleteDraftRequirement(requirement)}
                >
                  Delete Draft
                </button>
              </>
            )}
            {requirement.status === 'Active' && (
              <button
                type="button"
                className="action-button secondary-button"
                disabled={saving}
                onClick={() =>
                  void transitionRequirement(requirement, 'Archived')
                }
              >
                Archive
              </button>
            )}
            {requirement.status === 'Archived' && (
              <>
                <button
                  type="button"
                  className="action-button"
                  disabled={saving}
                  onClick={() =>
                    void transitionRequirement(requirement, 'Active')
                  }
                >
                  Reactivate
                </button>
                <button
                  type="button"
                  className="action-button secondary-button"
                  disabled={saving}
                  onClick={() =>
                    void transitionRequirement(requirement, 'Draft')
                  }
                >
                  Restore as Draft
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (group: StandardGroup, depth = 0): JSX.Element => {
    const isOpen = expanded.has(group.groupId);
    const children = [...group.groups, ...group.requirements];
    const childActionsAllowed =
      canEdit && currentStandard?.status !== 'Archived';
    return (
      <div
        className="compliance-item"
        key={group.groupId}
        style={{ marginLeft: depth * 12 }}
      >
        <button
          type="button"
          className="compliance-item-toggle"
          aria-expanded={isOpen}
          onClick={() => toggle(group.groupId)}
        >
          <span className="compliance-item-title-row">
            <strong>{group.name}</strong>
            <span className="compliance-item-badge compliance-item-badge--required">
              {group.status} · {children.length} item
              {children.length === 1 ? '' : 's'}
            </span>
          </span>
          <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
        </button>
        {isOpen && (
          <div className="compliance-item-body-inner">
            {group.description && (
              <p className="compliance-item-desc">{group.description}</p>
            )}
            <div className="compliance-list">
              {sortItems(group.requirements).map(renderRequirement)}
              {sortItems(group.groups).map(child =>
                renderGroup(child, depth + 1),
              )}
            </div>
            {childActionsAllowed && (
              <div className="inline-actions">
                {group.status !== 'Archived' && (
                  <>
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => startChildGroup(group)}
                    >
                      + Child control
                    </button>
                    <button
                      type="button"
                      className="action-button secondary-button"
                      onClick={() => startGroupEdit(group)}
                    >
                      Edit Control
                    </button>
                  </>
                )}
                {group.status === 'Draft' && (
                  <>
                    <button
                      type="button"
                      className="action-button"
                      disabled={saving}
                      onClick={() => void transitionGroup(group, 'Active')}
                    >
                      Activate
                    </button>
                    <button
                      type="button"
                      className="action-button danger-button"
                      disabled={saving}
                      onClick={() => void deleteDraftGroup(group)}
                    >
                      Delete Draft
                    </button>
                  </>
                )}
                {group.status === 'Active' && (
                  <button
                    type="button"
                    className="action-button secondary-button"
                    disabled={saving}
                    onClick={() => void transitionGroup(group, 'Archived')}
                  >
                    Archive control and contents
                  </button>
                )}
                {group.status === 'Archived' && (
                  <>
                    <button
                      type="button"
                      className="action-button"
                      disabled={saving}
                      onClick={() => void transitionGroup(group, 'Active')}
                    >
                      Reactivate control and contents
                    </button>
                    <button
                      type="button"
                      className="action-button secondary-button"
                      disabled={saving}
                      onClick={() => void transitionGroup(group, 'Draft')}
                    >
                      Restore control as Draft
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCreateStandardForm = () =>
    !showCreateStandard ? null : (
      <div className="compliance-add-form">
        <h4>Create Standard</h4>
        {(['name', 'framework', 'version', 'owner'] as const).map(field => (
          <label className="compliance-add-form-field" key={field}>
            <span>
              {field[0].toUpperCase() + field.slice(1)}
              {field === 'name' ? ' *' : ''}
            </span>
            <input
              className="form-control"
              value={standardForm[field]}
              onChange={event =>
                setStandardForm(previous => ({
                  ...previous,
                  [field]: event.target.value,
                }))
              }
            />
          </label>
        ))}
        <label className="compliance-add-form-field">
          <span>Description</span>
          <textarea
            className="form-control"
            value={standardForm.description}
            onChange={event =>
              setStandardForm(previous => ({
                ...previous,
                description: event.target.value,
              }))
            }
          />
        </label>
        <div className="inline-actions">
          <button
            type="button"
            className="action-button"
            disabled={saving}
            onClick={() => void createStandard()}
          >
            {saving ? 'Saving…' : 'Create'}
          </button>
          <button
            type="button"
            className="action-button secondary-button"
            onClick={() => setShowCreateStandard(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );

  const renderStandardEditForm = () =>
    !showStandardEdit ? null : (
      <div className="compliance-add-form">
        <h4>Edit Standard</h4>
        {(['name', 'framework', 'version', 'owner'] as const).map(field => (
          <label className="compliance-add-form-field" key={field}>
            <span>
              {field[0].toUpperCase() + field.slice(1)}
              {field === 'name' ? ' *' : ''}
            </span>
            <input
              className="form-control"
              value={standardForm[field]}
              onChange={event =>
                setStandardForm(previous => ({
                  ...previous,
                  [field]: event.target.value,
                }))
              }
            />
          </label>
        ))}
        <label className="compliance-add-form-field">
          <span>Description</span>
          <textarea
            className="form-control"
            value={standardForm.description}
            onChange={event =>
              setStandardForm(previous => ({
                ...previous,
                description: event.target.value,
              }))
            }
          />
        </label>
        <div className="inline-actions">
          <button
            type="button"
            className="action-button"
            disabled={saving}
            onClick={() => void updateStandard()}
          >
            {saving ? 'Saving…' : 'Save Standard'}
          </button>
          <button
            type="button"
            className="action-button secondary-button"
            onClick={() => setShowStandardEdit(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );

  const groupSubmitLabel = (() => {
    if (saving) return 'Saving…';
    if (editingGroupId) return 'Save Control';
    return 'Create Control';
  })();
  const requirementSubmitLabel = (() => {
    if (saving) return 'Saving…';
    if (editingRequirementId) return 'Save Requirement';
    return 'Create Requirement';
  })();

  const renderGroupForm = () =>
    !showGroupForm ? null : (
      <div ref={editFormRef} className="compliance-add-form">
        <h4>{editingGroupId ? 'Edit Control' : 'Create Control'}</h4>
        <label className="compliance-add-form-field">
          <span>Name *</span>
          <input
            className="form-control"
            value={groupForm.name}
            onChange={event =>
              setGroupForm(previous => ({
                ...previous,
                name: event.target.value,
              }))
            }
          />
        </label>
        <label className="compliance-add-form-field">
          <span>Description</span>
          <textarea
            className="form-control"
            value={groupForm.description}
            onChange={event =>
              setGroupForm(previous => ({
                ...previous,
                description: event.target.value,
              }))
            }
          />
        </label>
        <label className="compliance-add-form-field">
          <span>Parent Control</span>
          <select
            className="form-control"
            value={groupForm.parentGroupId}
            onChange={event =>
              setGroupForm(previous => ({
                ...previous,
                parentGroupId: event.target.value,
              }))
            }
          >
            <option value="">Top-level control</option>
            {groupOptionsForForm.map(({ group, depth }) => (
              <option key={group.groupId} value={group.groupId}>
                {'— '.repeat(depth)}
                {group.name}
              </option>
            ))}
          </select>
        </label>
        <div className="inline-actions">
          <button
            type="button"
            className="action-button"
            disabled={saving}
            onClick={() => void saveGroup()}
          >
            {groupSubmitLabel}
          </button>
          <button
            type="button"
            className="action-button secondary-button"
            onClick={() => {
              setShowGroupForm(false);
              setEditingGroupId(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );

  const renderRequirementForm = () =>
    !showRequirementForm ? null : (
      <div
        ref={editFormRef}
        className="compliance-add-form compliance-requirement-form"
      >
        <h4>
          {editingRequirementId ? 'Edit Requirement' : 'Create Requirement'}
        </h4>
        <label className="compliance-add-form-field">
          <span>Name *</span>
          <input
            className="form-control"
            value={requirementForm.name}
            onChange={event =>
              setRequirementForm(previous => ({
                ...previous,
                name: event.target.value,
              }))
            }
          />
        </label>
        <label className="compliance-add-form-field">
          <span>Description *</span>
          <textarea
            className="form-control"
            value={requirementForm.description}
            onChange={event =>
              setRequirementForm(previous => ({
                ...previous,
                description: event.target.value,
              }))
            }
          />
        </label>
        <label className="compliance-add-form-field">
          <span>Control</span>
          <select
            className="form-control"
            value={requirementForm.groupId}
            onChange={event =>
              setRequirementForm(previous => ({
                ...previous,
                groupId: event.target.value,
              }))
            }
          >
            <option value="">No control</option>
            {selectableGroupOptions.map(({ group, depth }) => (
              <option key={group.groupId} value={group.groupId}>
                {'— '.repeat(depth)}
                {group.name} ({group.status})
              </option>
            ))}
          </select>
        </label>
        <div className="inline-actions">
          <button
            type="button"
            className="action-button"
            disabled={saving}
            onClick={() => void saveRequirement()}
          >
            {requirementSubmitLabel}
          </button>
          <button
            type="button"
            className="action-button secondary-button"
            onClick={() => {
              setShowRequirementForm(false);
              setEditingRequirementId(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );

  const renderAssignmentPicker = () => {
    if (!showAssignments || !currentStandard) return null;
    const filtered = applications.filter(application => {
      const text = `${application.number ?? application.ban ?? ''} ${
        application.name ?? ''
      } ${application.lifecycle ?? ''} ${application.business_unit ?? ''} ${
        application.department ?? ''
      }`.toLowerCase();
      return text.includes(applicationSearch.toLowerCase());
    });
    // Applications the Standard applies to automatically (Baseline matrix).
    // Those without a manual assignment are read-only here: there is no
    // assignment to save or remove, so they are shown checked and disabled.
    const automaticBans = new Set(automaticAssignments.map(item => item.ban));
    const manualBans = new Set(assignments.map(item => item.ban));
    const isAutomaticOnly = (ban: string) =>
      automaticBans.has(ban) && !manualBans.has(ban);
    const totalAssigned = new Set([
      ...Array.from(manualBans),
      ...Array.from(automaticBans),
    ]).size;

    const visibleBans = Array.from(
      new Set(
        filtered
          .map(application => application.number ?? application.ban ?? '')
          .filter(ban => Boolean(ban) && !isAutomaticOnly(ban)),
      ),
    );
    const selectedVisibleCount = visibleBans.filter(ban =>
      selectedBans.has(ban),
    ).length;
    const allVisibleSelected =
      visibleBans.length > 0 && selectedVisibleCount === visibleBans.length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

    const toggleAllVisible = () => {
      setSelectedBans(previous => {
        const next = new Set(previous);
        if (allVisibleSelected) {
          visibleBans.forEach(ban => next.delete(ban));
        } else {
          visibleBans.forEach(ban => next.add(ban));
        }
        return next;
      });
    };

    return (
      <div className="compliance-add-form">
        <h4>Assign to applications</h4>
        <input
          className="form-control compliance-search-input"
          placeholder="Search applications"
          value={applicationSearch}
          onChange={event => setApplicationSearch(event.target.value)}
        />
        <div className="compliance-assignment-filters">
          <input
            className="form-control"
            placeholder="BAN"
            value={applicationFilters.ban}
            onChange={event =>
              updateApplicationFilter('ban', event.target.value)
            }
          />
          <input
            className="form-control"
            placeholder="Application name"
            value={applicationFilters.name}
            onChange={event =>
              updateApplicationFilter('name', event.target.value)
            }
          />
          <select
            className="form-control"
            value={applicationFilters.lifecycle}
            onChange={event =>
              updateApplicationFilter('lifecycle', event.target.value)
            }
          >
            <option value="">Lifecycle</option>
            {applicationFilterOptions.lifecycle.map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            className="form-control"
            value={applicationFilters.securityImpact}
            onChange={event =>
              updateApplicationFilter('securityImpact', event.target.value)
            }
          >
            <option value="">Security impact</option>
            {applicationFilterOptions.securityImpact.map(value => (
              <option key={String(value)} value={String(value)}>
                {String(value)}
              </option>
            ))}
          </select>
          <select
            className="form-control"
            value={applicationFilters.businessUnit}
            onChange={event =>
              updateApplicationFilter('businessUnit', event.target.value)
            }
          >
            <option value="">Business unit</option>
            {applicationFilterOptions.businessUnit.map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            className="form-control"
            value={applicationFilters.department}
            onChange={event =>
              updateApplicationFilter('department', event.target.value)
            }
          >
            <option value="">Department</option>
            {applicationFilterOptions.department.map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <label className="compliance-select-all">
          <input
            type="checkbox"
            ref={checkbox => {
              if (checkbox) checkbox.indeterminate = someVisibleSelected;
            }}
            checked={allVisibleSelected}
            disabled={visibleBans.length === 0 || saving}
            onChange={toggleAllVisible}
          />{' '}
          Select all visible applications ({visibleBans.length})
        </label>
        <div className="compliance-assignment-list">
          {filtered.map(application => {
            const ban = application.number ?? application.ban ?? '';
            const automaticOnly = isAutomaticOnly(ban);
            return (
              <label key={ban}>
                <input
                  type="checkbox"
                  checked={automaticOnly || selectedBans.has(ban)}
                  disabled={automaticOnly}
                  onChange={() => toggleAssignment(ban)}
                />{' '}
                {ban} — {application.name ?? 'Unnamed application'}
                {automaticBans.has(ban) && (
                  <em> (applied automatically by app type and impact)</em>
                )}
              </label>
            );
          })}
        </div>
        <div className="inline-actions">
          <button
            type="button"
            className="action-button"
            disabled={saving}
            onClick={() => void saveAssignments()}
          >
            Save assignments
          </button>
          <span>
            {automaticBans.size > 0
              ? `${totalAssigned} currently assigned (${automaticBans.size} automatic, ${manualBans.size} manual)`
              : `${manualBans.size} currently assigned`}
          </span>
        </div>
      </div>
    );
  };

  const renderStandard = (standard: Standard) => (
    <div className="card detail-view-card">
      <div className="detail-view-header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigateTo('dashboard')}
        >
          <span aria-hidden="true">←</span> Back to Standards
        </button>
        <h4 className="detail-view-title">
          <span className="dashboard-card-icon">
            <CardIcon standard={standard} />
          </span>{' '}
          {standard.name}
        </h4>
        <p>
          {standard.framework} · {standard.version} · Owner: {standard.owner}
        </p>
      </div>
      {standard.description && <p>{standard.description}</p>}
      {canEdit && (
        <div className="inline-actions">
          {standard.status !== 'Archived' && (
            <button
              type="button"
              className="action-button"
              onClick={() => startStandardEdit(standard)}
            >
              Edit Standard
            </button>
          )}
          {standard.status === 'Draft' && (
            <>
              <button
                type="button"
                className="action-button"
                disabled={saving}
                onClick={() => void transitionStandard('Active')}
              >
                Activate
              </button>
              <button
                type="button"
                className="action-button danger-button"
                disabled={saving}
                onClick={() => void deleteDraftStandard()}
              >
                Delete Draft
              </button>
            </>
          )}
          {standard.status === 'Active' && (
            <button
              type="button"
              className="action-button secondary-button"
              disabled={saving}
              onClick={() => void transitionStandard('Archived')}
            >
              Archive Standard
            </button>
          )}
          {standard.status === 'Archived' && (
            <>
              <button
                type="button"
                className="action-button"
                disabled={saving}
                onClick={() => void transitionStandard('Active')}
              >
                Reactivate
              </button>
              <button
                type="button"
                className="action-button secondary-button"
                disabled={saving}
                onClick={() => void transitionStandard('Draft')}
              >
                Restore as Draft
              </button>
            </>
          )}
          {standard.status !== 'Archived' && (
            <>
              <button
                type="button"
                className="action-button"
                onClick={() => {
                  setEditingGroupId(null);
                  setGroupForm({
                    name: '',
                    description: '',
                    parentGroupId: '',
                  });
                  setShowGroupForm(value => !value);
                  setShowRequirementForm(false);
                  setShowStandardEdit(false);
                }}
              >
                + Control
              </button>
              <button
                type="button"
                className="action-button"
                onClick={() => {
                  setEditingRequirementId(null);
                  setRequirementForm({
                    name: '',
                    description: '',
                    groupId: '',
                  });
                  setShowRequirementForm(value => !value);
                  setShowGroupForm(false);
                  setShowStandardEdit(false);
                }}
              >
                + Requirement
              </button>
            </>
          )}
          {standard.status === 'Active' && (
            <button
              type="button"
              className="action-button secondary-button"
              onClick={() => {
                setShowAssignments(true);
                setShowStandardEdit(false);
                setShowGroupForm(false);
                setShowRequirementForm(false);
                void loadAssignments();
              }}
            >
              Assign to applications
            </button>
          )}
        </div>
      )}
      {renderStandardEditForm()}
      {renderGroupForm()}
      {renderRequirementForm()}
      {renderAssignmentPicker()}
      {formError && <p className="monitoring-error-notice">{formError}</p>}
      {standard.requirements.length > 0 && (
        <>
          <h5>Requirements</h5>
          <div className="compliance-list">
            {sortItems(standard.requirements).map(renderRequirement)}
          </div>
        </>
      )}
      <h5>Controls</h5>
      {standard.groups.length === 0 ? (
        <p className="compliance-empty-state">No controls yet.</p>
      ) : (
        <div className="compliance-list">
          {sortItems(standard.groups).map(group => renderGroup(group))}
        </div>
      )}
      <p className="compliance-filter-summary">
        Created by {formatUserRef(standard.createdBy)} · Status:{' '}
        {standard.status}
      </p>
    </div>
  );

  const renderDashboard = () => {
    const draftStandards = sortItems(
      DATA.standards.filter(standard => standard.status === 'Draft'),
    );
    const archivedStandards = sortItems(
      DATA.standards.filter(standard => standard.status === 'Archived'),
    );
    return (
      <>
        <div className="primary_card">
          <h4>Standards Library</h4>
          <p>
            Browse active security and compliance Standards, manage recursive
            Controls and Requirements, and assign Standards to applications.
          </p>
          {canEdit && (
            <>
              <div className="inline-actions">
                <button
                  type="button"
                  className="action-button"
                  onClick={() => setShowCreateStandard(value => !value)}
                >
                  + Create Standard
                </button>
              </div>
              {renderCreateStandardForm()}
            </>
          )}
        </div>
        <div className="card modules-card">
          <h4>Standards</h4>
          <div className="dashboard-grid" role="list">
            {activeStandards.map(standard => (
              <button
                key={standard.standardId}
                type="button"
                className="dashboard-card"
                onClick={() => navigateTo(standard.standardId)}
              >
                <span className="dashboard-card-icon" aria-hidden="true">
                  <CardIcon standard={standard} />
                </span>
                <span className="dashboard-card-label">{standard.name}</span>
                <small>
                  {countGroups(standard.groups)} controls ·{' '}
                  {standard.requirements.length} direct requirements
                </small>
              </button>
            ))}
          </div>
          {activeStandards.length === 0 && (
            <p className="compliance-empty-state">
              No active Standards are available.
            </p>
          )}
        </div>
        {draftStandards.length > 0 && (
          <div className="card modules-card">
            <h4>Draft Standards</h4>
            <div className="compliance-draft-list">
              {draftStandards.map(standard => (
                <button
                  key={standard.standardId}
                  type="button"
                  className="action-button secondary-button"
                  onClick={() => navigateTo(standard.standardId)}
                >
                  {standard.name} · Draft
                </button>
              ))}
            </div>
          </div>
        )}
        {archivedStandards.length > 0 && (
          <div className="card modules-card">
            <h4>Archived Standards</h4>
            <div className="compliance-draft-list">
              {archivedStandards.map(standard => (
                <button
                  key={standard.standardId}
                  type="button"
                  className="action-button secondary-button"
                  onClick={() => navigateTo(standard.standardId)}
                >
                  {standard.name} · Archived
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  if (loading && !data)
    return (
      <Page themeId="tool">
        <Header title="Compliance Management" />
        <Content>
          <p className="loading-text">Loading Standards Library…</p>
        </Content>
      </Page>
    );
  if (!data)
    return (
      <Page themeId="tool">
        <Header title="Compliance Management" />
        <Content>
          <p className="monitoring-error-notice">
            {error ?? 'The Standards Library is unavailable.'}
          </p>
        </Content>
      </Page>
    );
  return (
    <Page themeId="tool">
      <style>{COMPLIANCE_PAGE_STYLES}</style>
      <Header title="Compliance Management" />
      <Content>
        <div className="detail-container">
          <Routes>
            <Route index element={renderDashboard()} />
            <Route
              path=":standardId"
              element={
                currentStandard
                  ? renderStandard(currentStandard)
                  : renderDashboard()
              }
            />
          </Routes>
        </div>

        {/* Link Picker Modal */}
        {showLinkPicker && (
          <div className="modal-overlay">
            <button
              type="button"
              className="modal-overlay-backdrop"
              aria-label="Close dialog"
              onClick={() => setShowLinkPicker(false)}
            />
            <div
              className="modal-content"
              role="dialog"
              aria-modal="true"
              aria-label="Link to another standard"
            >
              <div className="modal-header">
                <h3>Link to another standard</h3>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setShowLinkPicker(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="link-target-filters">
                  <div className="form-field">
                    <label htmlFor="link-target-standard">Standard:</label>
                    <select
                      id="link-target-standard"
                      className="form-control"
                      value={linkStandardFilter}
                      onChange={e => changeLinkStandardFilter(e.target.value)}
                    >
                      <option value="">All active standards</option>
                      {linkStandardOptions.map(standard => (
                        <option
                          key={standard.standardId}
                          value={standard.standardId}
                        >
                          {standard.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label htmlFor="link-target-control">Control group:</label>
                    <select
                      id="link-target-control"
                      className="form-control"
                      value={linkGroupFilter}
                      onChange={e => changeLinkGroupFilter(e.target.value)}
                      disabled={!linkStandardFilter}
                    >
                      <option value="">
                        {linkStandardFilter
                          ? 'All control groups'
                          : 'Select a standard first'}
                      </option>
                      {linkControlOptions.map(({ group, depth }) => (
                        <option key={group.groupId} value={group.groupId}>
                          {'— '.repeat(depth)}
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="link-target-search">
                    Search for a requirement:
                  </label>
                  <input
                    id="link-target-search"
                    className="link-target-search-input"
                    type="text"
                    value={linkTargetSearch}
                    onChange={e => setLinkTargetSearch(e.target.value)}
                    placeholder="Search by requirement name or reference"
                  />
                </div>
                <div className="link-target-list">
                  {getLinkTargetCandidates()
                    .slice(0, 20) // Limit results
                    .map(req => (
                      <div
                        key={req.requirementId}
                        className={`link-target-option ${
                          selectedLinkTarget?.requirementId ===
                          req.requirementId
                            ? 'selected'
                            : ''
                        }`}
                        role="button"
                        aria-pressed={
                          selectedLinkTarget?.requirementId ===
                          req.requirementId
                        }
                        tabIndex={0}
                        onClick={() => setSelectedLinkTarget(req)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedLinkTarget(req);
                          }
                        }}
                      >
                        <div className="target-info">
                          <strong>{req.ref}</strong> - {req.name}
                          <div className="target-standard">
                            {(req as any).standardName}
                          </div>
                        </div>
                        {selectedLinkTarget?.requirementId ===
                          req.requirementId && (
                          <span className="link-target-selected-label">
                            <span aria-hidden="true">✓</span> Selected
                          </span>
                        )}
                      </div>
                    ))}
                </div>

                {selectedLinkTarget && (
                  <div className="content-selection">
                    <h4>Content Selection</h4>
                    <p>
                      Which requirement's content should be used for both linked
                      requirements?
                    </p>
                    <div className="content-options">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="useContentFrom"
                          value={linkingRequirementId || ''}
                          checked={useContentFrom === linkingRequirementId}
                          onChange={e => setUseContentFrom(e.target.value)}
                        />
                        <div>
                          <strong>Current requirement:</strong>{' '}
                          {
                            DATA.standards
                              .flatMap(s => [
                                ...(s.requirements || []),
                                ...s.groups.flatMap(g => g.requirements || []),
                              ])
                              .find(
                                r => r.requirementId === linkingRequirementId,
                              )?.name
                          }
                        </div>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="useContentFrom"
                          value={selectedLinkTarget.requirementId}
                          checked={
                            useContentFrom === selectedLinkTarget.requirementId
                          }
                          onChange={e => setUseContentFrom(e.target.value)}
                        />
                        <div>
                          <strong>Target requirement:</strong>{' '}
                          {selectedLinkTarget.name}
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {formError && <div className="form-error">{formError}</div>}
                <button
                  type="button"
                  className="action-button secondary-button"
                  onClick={() => setShowLinkPicker(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="action-button"
                  onClick={confirmLink}
                  disabled={!selectedLinkTarget || !useContentFrom || saving}
                >
                  {saving ? 'Linking...' : 'Confirm Link'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Content>
    </Page>
  );
}
